/**
 * PayrollReasonCodePicker - Wrapper around generic EntityPicker
 * 
 * Provides company-specific configuration and maintains backward compatibility
 * with any code that imports PayrollReasonCodePickerField or PayrollReasonCodePickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';
import { EntityPickerSelect } from '../EntityPicker/EntityPickerSelect';

// ── PayrollReasonCode-specific configuration ────────────────────────────────────────────
export const PAYROLLREASONCODE_CONFIG = {
  apiEndpoint: "/api/PayrollReasonCode/active",
  entityName: "PayrollReasonCode",
  idField: "id",
  displayFields: {
    primary: "payrollCode",
    secondary: "fieldName"
  },
  tableHeaders: ["Description"],
  tableAccessors: ["description"],
  searchFields: [
    { label: "Description", field: "description", index: 0 },
  ],
  placeholder: "--Select company--"
};


// ── PayrollReasonCodePickerSelect wrapper ────────────────────────────────────────────────
/**
 * PayrollReasonCodePickerSelect
 * 
 * Usage:
 * <PayrollReasonCodePickerSelect
 *   value={selectedPayrollReasonCodeObject}
 *   onChange={(val) => setPayrollReasonCode(val)}
 *   placeholder="All"
 * />
 */
export function PayrollReasonCodePickerSelect({ value, onChange, ...rest }) {
  return (
    <EntityPickerSelect
      {...rest}
      config={PAYROLLREASONCODE_CONFIG}
      value={value}
      onChange={onChange}
    />
  );
}


// ── PayrollReasonCodePickerField wrapper ────────────────────────────────────────────────
/**
 * PayrollReasonCodePickerField
 * 
 * Usage:
 * <PayrollReasonCodePickerField
 *   label="PayrollReasonCode"
 *   companyId={form.companyId}
 *   displayValue={form.companyDescription}
 *   onSelect={(company) => {
 *     set("companyId", company.id);
 *     set("companyDescription", company.description);
 *   }}
 *   onClear={() => {
 *     set("companyId", "");
 *     set("companyDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function PayrollReasonCodePickerField({
  companyId,           // company-specific prop name
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {
  return (
    <EntityPickerField
      {...rest}
      config={PAYROLLREASONCODE_CONFIG}
      entityId={companyId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── PayrollReasonCodePickerModal wrapper ────────────────────────────────────────────────
/**
 * PayrollReasonCodePickerModal
 * 
 * Usage (standalone):
 * <PayrollReasonCodePickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(company) => {
 *     set("companyId", company.id);
 *     set("companyDescription", company.description);
 *   }}
 * />
 */
export function PayrollReasonCodePickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={PAYROLLREASONCODE_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { PayrollReasonCodePickerField as default };